import React, { useEffect, useRef } from "react";
import { Info, Sparkles, Download, Settings as SettingsIcon, Database } from "lucide-react";

/* ═══════════════════════════════════════════════════════════════════
   AI Cost & Results Dashboard — Customer Portal
   Spec: emergent_dashboard_brief.md (Parts 1–13)

   • Real data: tenant.organization_name + tenant.confirmed_workflows
   • Everything else is intentional placeholder (mockup banner shown)
   • Renders inside the existing Customer Portal shell — no sidebar/topbar rebuild
   • Locked by default; unlocks ONLY when scope_payment_complete === true
   ════════════════════════════════════════════════════════════════════ */

const TOK = {
  blue: "#3B4FE4",
  blueL: "#EEF0FD",
  blueD: "#2D3DB8",
  green: "#1A6B42",
  greenL: "#E4F3EC",
  amber: "#C97B1A",
  amberL: "#FEF3DC",
  red: "#B83232",
  redL: "#FDECEA",
  gold: "#E8A020",
  ink: "#0F1117",
  ink2: "#2D3142",
  ink3: "#5C6070",
  ink4: "#9499A8",
  rule: "rgba(0,0,0,0.07)",
  rule2: "rgba(0,0,0,0.12)",
  bg: "#FAFAF8",
  card: "#FFFFFF",
};

/* ── KPI strip (5 cards) ── */
const KPI_DATA = [
  { label: "Monthly AI Spend",   value: "$3,820",  delta: "−12%", deltaColor: TOK.green, sub: "vs last month" },
  { label: "Cost / Workflow",    value: "$955",    delta: "Healthy", deltaColor: TOK.green, sub: "monthly avg" },
  { label: "Hours Freed",        value: "412",     delta: "+58", deltaColor: TOK.green, sub: "this month" },
  { label: "Labor Value Created",value: "$24,720", delta: "+$3.2K", deltaColor: TOK.green, sub: "team capacity" },
  { label: "Net ROI Multiple",   value: "6.5×",    delta: "+0.9×", deltaColor: TOK.green, sub: "spend → value" },
];

const ROI_BAND = [
  { v: "$3,820",  l: "Monthly AI Cost" },
  { v: "$24,720", l: "Labor Value Created" },
  { v: "+$20,900",l: "Net Monthly Value", color: "#fde68a" },
  { v: "6.5×",    l: "Return on AI Spend" },
];

/* ── Cost trend chart (Feb-Jun) ── */
const TREND = [
  { m: "Feb", v: 4200 },
  { m: "Mar", v: 4060 },
  { m: "Apr", v: 4340 },
  { m: "May", v: 3820 },
  { m: "Jun", v: 3650, projected: true },
];

const drawTrendChart = (canvas) => {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth;
  const H = canvas.clientHeight;
  canvas.width = W * dpr;
  canvas.height = H * dpr;
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, W, H);

  const padX = 32, padTop = 18, padBot = 28;
  const innerW = W - padX * 2;
  const innerH = H - padTop - padBot;
  const max = Math.max(...TREND.map((d) => d.v));
  const min = Math.min(...TREND.map((d) => d.v));
  const range = (max - min) || 1;

  // grid lines
  ctx.strokeStyle = TOK.rule;
  ctx.lineWidth = 1;
  for (let i = 0; i < 4; i++) {
    const y = padTop + (innerH / 3) * i;
    ctx.beginPath();
    ctx.moveTo(padX, y);
    ctx.lineTo(W - padX, y);
    ctx.stroke();
  }

  // points
  const pts = TREND.map((d, i) => {
    const x = padX + (innerW / (TREND.length - 1)) * i;
    const y = padTop + innerH - ((d.v - min) / range) * innerH;
    return { x, y, ...d };
  });

  // line
  ctx.strokeStyle = TOK.blue;
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  pts.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();

  // dots + labels
  pts.forEach((p) => {
    ctx.fillStyle = p.projected ? TOK.amber : TOK.blue;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = TOK.ink3;
    ctx.font = "500 11px DM Sans, system-ui";
    ctx.textAlign = "center";
    ctx.fillText(p.m, p.x, H - 8);
  });
};

/* ── Workflow card colour cycle ── */
const CARD_COLORS = [
  { bg: TOK.greenL, accent: TOK.green },
  { bg: TOK.blueL,  accent: TOK.blue },
  { bg: TOK.amberL, accent: TOK.amber },
  { bg: "#FBE9C8",  accent: TOK.gold },
];

/* ── A small reusable card ── */
const Card = ({ children, style = {}, className = "", ...rest }) => (
  <div
    className={`rounded-[10px] border bg-white ${className}`}
    style={{ borderColor: TOK.rule, ...style }}
    {...rest}
  >
    {children}
  </div>
);

const SectionTitle = ({ eyebrow, title }) => (
  <div className="mb-5">
    <div className="text-[11px] font-bold tracking-[0.14em] uppercase" style={{ color: TOK.ink3 }}>
      {eyebrow}
    </div>
    <h2 className="mt-1 text-[22px] font-bold tracking-tight" style={{ color: TOK.ink, letterSpacing: "-0.01em" }}>
      {title}
    </h2>
  </div>
);

const SectionRule = () => (
  <div style={{ height: 1, background: TOK.rule, margin: "44px 0 36px" }} />
);

/* ════════════════════════════════════════════════════════════════ */
export default function CustomerCostDashboard({ tenant }) {
  const orgName = tenant?.organization_name || tenant?.name || "Your Organization";
  const confirmed = (tenant?.prospect?.confirmed_workflows || tenant?.confirmed_workflows || [])
    .map((w) => (typeof w === "string" ? { id: w, name: w } : w));
  // fallback if no confirmed workflows captured yet — keep mockup readable
  const workflows = confirmed.length
    ? confirmed.slice(0, 4)
    : [
        { id: "w1", name: "Workflow 1" },
        { id: "w2", name: "Workflow 2" },
        { id: "w3", name: "Workflow 3" },
      ];

  const chartRef = useRef(null);

  useEffect(() => {
    const draw = () => drawTrendChart(chartRef.current);
    draw();
    window.addEventListener("resize", draw);
    return () => window.removeEventListener("resize", draw);
  }, []);

  return (
    <div
      className="w-full"
      style={{
        background: TOK.bg,
        fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
        color: TOK.ink2,
        padding: "28px 32px 60px",
        minHeight: "calc(100vh - 60px)",
      }}
      data-testid="customer-cost-dashboard"
    >
      {/* TOPBAR (lightweight — main shell already provides outer chrome) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="h-2 w-2 rounded-full inline-block" style={{ background: TOK.green, boxShadow: `0 0 0 4px ${TOK.greenL}` }} />
            <h1 className="text-[20px] font-bold tracking-tight" style={{ color: TOK.ink, letterSpacing: "-0.01em" }}>
              AI Cost &amp; Results
            </h1>
            <span className="text-[13px]" style={{ color: TOK.ink3 }}>· {orgName}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] border text-[12.5px] font-semibold" style={{ borderColor: TOK.rule2, color: TOK.ink2, background: "#fff" }}>
            <Download className="h-3.5 w-3.5" /> Export
          </button>
          <button className="inline-flex items-center gap-1.5 h-9 px-3 rounded-[8px] border text-[12.5px] font-semibold" style={{ borderColor: TOK.rule2, color: TOK.ink2, background: "#fff" }}>
            <SettingsIcon className="h-3.5 w-3.5" /> Configure
          </button>
        </div>
      </div>

      {/* MOCKUP BANNER */}
      <div
        className="flex items-start gap-2.5 mb-6 rounded-[10px]"
        style={{
          background: TOK.amberL,
          border: `1px solid rgba(201,123,26,0.30)`,
          padding: "12px 18px",
          color: "#5C4010",
          fontSize: 13,
          lineHeight: 1.5,
        }}
        data-testid="dashboard-mockup-banner"
      >
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: TOK.amber }} />
        <div>
          <strong>Dashboard Preview</strong> — Metrics shown are illustrative
          placeholders. Live data tracking activates once your first workflow
          goes live. Your workflow names and project details are real.
        </div>
      </div>

      {/* ════════════ EXECUTIVE SUMMARY ════════════ */}
      <SectionTitle eyebrow="Executive Summary" title="Where you stand this month" />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="dashboard-kpi-strip">
        {KPI_DATA.map((k) => (
          <Card key={k.label} className="p-4">
            <div className="text-[11px] font-bold tracking-[0.10em] uppercase" style={{ color: TOK.ink3 }}>
              {k.label}
            </div>
            <div className="mt-2 text-[28px] font-bold leading-none" style={{ color: TOK.ink, letterSpacing: "-0.02em" }}>
              {k.value}
            </div>
            <div className="mt-2.5 flex items-center gap-2 text-[12px]">
              <span className="font-semibold" style={{ color: k.deltaColor }}>{k.delta}</span>
              <span style={{ color: TOK.ink3 }}>{k.sub}</span>
            </div>
          </Card>
        ))}
      </div>

      {/* ROI band */}
      <div
        className="rounded-[10px] grid grid-cols-2 sm:grid-cols-4 divide-x"
        style={{ background: TOK.ink, borderColor: "rgba(255,255,255,0.10)" }}
        data-testid="dashboard-roi-band"
      >
        {ROI_BAND.map((r, i) => (
          <div key={r.l} className="px-5 py-5" style={{ borderColor: "rgba(255,255,255,0.10)" }}>
            <div className="text-[11px] font-bold tracking-[0.12em] uppercase" style={{ color: "rgba(255,255,255,0.55)" }}>
              {r.l}
            </div>
            <div className="mt-2 text-[30px] font-bold leading-none" style={{ color: r.color || "#fff", letterSpacing: "-0.02em" }}>
              {r.v}
            </div>
          </div>
        ))}
      </div>

      <SectionRule />

      {/* ════════════ SECTION 01 — AI Operating Costs ════════════ */}
      <SectionTitle eyebrow="Section 01" title="AI Operating Costs" />
      <div className="flex items-center gap-2 mb-4 text-[12px]" data-testid="dashboard-month-tabs">
        {["Apr", "May", "Jun (projected)"].map((m, i) => (
          <button
            key={m}
            className="px-3 h-8 rounded-full border font-semibold"
            style={{
              background: i === 1 ? TOK.ink : "#fff",
              color: i === 1 ? "#fff" : TOK.ink3,
              borderColor: i === 1 ? TOK.ink : TOK.rule2,
            }}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-4 mb-2" data-testid="dashboard-cost-section">
        {/* Cost breakdown table */}
        <Card className="p-5">
          <div className="flex items-baseline justify-between mb-1">
            <div className="text-[14px] font-bold" style={{ color: TOK.ink }}>Cost breakdown by workflow</div>
            <div className="text-[12px]" style={{ color: TOK.ink3 }}>May 2026</div>
          </div>
          <div className="text-[14px]" style={{ color: TOK.ink3 }}>How your AI spend is allocated across workflows + baseline infra.</div>
          <table className="mt-4 w-full text-left">
            <thead>
              <tr style={{ borderBottom: `1px solid ${TOK.rule}` }}>
                {["Workflow", "Tokens", "Cost", "% of total"].map((h, i) => (
                  <th key={h} className="py-2.5 text-[11px] font-bold tracking-[0.10em] uppercase" style={{ color: TOK.ink3, textAlign: i >= 1 ? "right" : "left" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workflows.map((wf, i) => {
                const sample = [
                  { tokens: "2.4M", cost: "$1,210", pct: "32%" },
                  { tokens: "1.6M", cost: "$840",   pct: "22%" },
                  { tokens: "1.1M", cost: "$510",   pct: "13%" },
                  { tokens: "0.7M", cost: "$320",   pct: "8%"  },
                ][i] || { tokens: "—", cost: "—", pct: "—" };
                return (
                  <tr key={wf.id || wf.name} style={{ borderBottom: `1px solid ${TOK.rule}` }}>
                    <td className="py-3 text-[14px]" style={{ color: TOK.ink2 }}>{wf.name}</td>
                    <td className="py-3 text-[14px] text-right" style={{ color: TOK.ink2 }}>{sample.tokens}</td>
                    <td className="py-3 text-[14px] text-right font-semibold" style={{ color: TOK.ink }}>{sample.cost}</td>
                    <td className="py-3 text-[14px] text-right" style={{ color: TOK.ink3 }}>{sample.pct}</td>
                  </tr>
                );
              })}
              <tr>
                <td className="py-3 text-[14px]" style={{ color: TOK.ink3 }}>Infrastructure / Embeddings</td>
                <td className="py-3 text-[14px] text-right" style={{ color: TOK.ink3 }}>—</td>
                <td className="py-3 text-[14px] text-right font-semibold" style={{ color: TOK.ink }}>$940</td>
                <td className="py-3 text-[14px] text-right" style={{ color: TOK.ink3 }}>25%</td>
              </tr>
            </tbody>
          </table>
        </Card>

        {/* Right column */}
        <div className="space-y-4">
          <Card className="p-5" data-testid="dashboard-provider-card">
            <div className="text-[14px] font-bold" style={{ color: TOK.ink }}>Provider breakdown</div>
            <div className="text-[12.5px]" style={{ color: TOK.ink3 }}>{workflows.length} workflows · 2 providers</div>
            <div className="mt-4 space-y-3">
              {[
                { name: "OpenAI GPT-4o", pct: 64, color: TOK.blue },
                { name: "Anthropic Claude", pct: 26, color: TOK.green },
                { name: "Embeddings & infra", pct: 10, color: TOK.amber },
              ].map((p) => (
                <div key={p.name}>
                  <div className="flex justify-between text-[13px]" style={{ color: TOK.ink2 }}>
                    <span>{p.name}</span>
                    <span className="font-semibold">{p.pct}%</span>
                  </div>
                  <div className="mt-1.5 h-[6px] rounded-full" style={{ background: TOK.rule }}>
                    <div className="h-full rounded-full" style={{ width: `${p.pct}%`, background: p.color }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <div className="text-[14px] font-bold" style={{ color: TOK.ink }}>Monthly cost trend</div>
            <div className="text-[12.5px] mb-3" style={{ color: TOK.ink3 }}>Feb–Jun 2026 (projected)</div>
            <canvas ref={chartRef} style={{ width: "100%", height: 140, display: "block" }} data-testid="dashboard-cost-chart" />
          </Card>

          <Card className="p-5" data-testid="dashboard-optimization-flags">
            <div className="text-[14px] font-bold mb-2" style={{ color: TOK.ink }}>Optimization flags</div>
            <ul className="space-y-2.5 text-[13px]" style={{ color: TOK.ink2 }}>
              <li className="flex gap-2"><span style={{ color: TOK.green }}>●</span> Provider routing reduced spend by ~12% MoM.</li>
              <li className="flex gap-2"><span style={{ color: TOK.amber }}>●</span> Caching opportunity on repeat queries (~$180/mo).</li>
              <li className="flex gap-2"><span style={{ color: TOK.blue }}>●</span> Move 2 prompts to a smaller model where eval-equal.</li>
            </ul>
          </Card>
        </div>
      </div>

      <SectionRule />

      {/* ════════════ SECTION 02 — Workflow Results ════════════ */}
      <SectionTitle eyebrow="Section 02" title="Real-time performance" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-workflow-cards">
        {workflows.map((wf, i) => {
          const c = CARD_COLORS[i % CARD_COLORS.length];
          const live = i < 2;
          const sample = [
            { kpis: [["Hours freed", "168 hrs"], ["Resolution time", "−54%"], ["Quality score", "94/100"]] },
            { kpis: [["Hours freed", "104 hrs"], ["Throughput", "+38%"], ["Error rate", "−61%"]] },
            { kpis: [["Hours freed", "72 hrs"], ["Cycle time", "−42%"], ["Backlog", "−28%"]] },
            { kpis: [["Hours freed", "38 hrs"], ["First-touch SLA", "97%"], ["Customer CSAT", "4.7/5"]] },
          ][i] || { kpis: [["Hours freed", "—"], ["Status", "—"], ["—", "—"]] };
          return (
            <div
              key={wf.id || wf.name}
              className="rounded-[10px] p-5 border"
              style={{ background: c.bg, borderColor: TOK.rule }}
            >
              <div className="flex items-center justify-between">
                <div className="text-[15px] font-bold leading-tight" style={{ color: TOK.ink, letterSpacing: "-0.01em" }}>
                  {wf.name}
                </div>
                <span
                  className="px-2 py-0.5 rounded-full text-[10.5px] font-bold tracking-wider"
                  style={{
                    background: live ? "#fff" : "rgba(255,255,255,0.55)",
                    color: live ? c.accent : TOK.ink3,
                    border: `1px solid ${live ? c.accent : TOK.rule2}`,
                  }}
                >
                  {live ? "LIVE" : "IN PREP"}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {sample.kpis.map(([label, value], j) => (
                  <div key={j}>
                    <div className="text-[11px] font-bold tracking-[0.10em] uppercase" style={{ color: TOK.ink3 }}>{label}</div>
                    <div className="text-[22px] font-bold leading-none" style={{ color: TOK.ink, letterSpacing: "-0.02em" }}>{value}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <SectionRule />

      {/* ════════════ SECTION 03 — Cumulative Impact ════════════ */}
      <SectionTitle eyebrow="Section 03" title="Cumulative impact (last 90 days)" />
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        {[
          { title: "Hours freed", subtitle: "Per workflow", color: TOK.blue,
            rows: workflows.map((w, i) => ({ l: w.name, v: [412, 268, 184, 112][i] || 60, max: 412 })) },
          { title: "Labor value created", subtitle: "Per workflow", color: TOK.green,
            rows: workflows.map((w, i) => ({ l: w.name, v: [24720, 16080, 11040, 6720][i] || 4200, max: 24720, fmt: (n) => `$${n.toLocaleString()}` })) },
          { title: "Spend vs. value", subtitle: "Net result", color: TOK.amber,
            rows: [
              { l: "AI Spend",  v: 11460, max: 78000, fmt: (n) => `$${n.toLocaleString()}` },
              { l: "Labor Value", v: 74160, max: 78000, fmt: (n) => `$${n.toLocaleString()}` },
              { l: "Net Value", v: 62700, max: 78000, fmt: (n) => `$${n.toLocaleString()}` },
            ] },
        ].map((blk) => (
          <Card key={blk.title} className="p-5">
            <div className="text-[14px] font-bold" style={{ color: TOK.ink }}>{blk.title}</div>
            <div className="text-[12.5px] mb-4" style={{ color: TOK.ink3 }}>{blk.subtitle}</div>
            <div className="space-y-3">
              {blk.rows.map((r) => (
                <div key={r.l}>
                  <div className="flex justify-between text-[13px]" style={{ color: TOK.ink2 }}>
                    <span className="truncate pr-2">{r.l}</span>
                    <span className="font-semibold">{r.fmt ? r.fmt(r.v) : r.v}</span>
                  </div>
                  <div className="mt-1.5 h-[8px] rounded-full" style={{ background: TOK.rule }}>
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, (r.v / r.max) * 100)}%`, background: blk.color }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Qualitative outcome flags */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { title: "Team feedback", body: "Staff report less context-switching and faster handoffs across workflows.", color: TOK.greenL, accent: TOK.green },
          { title: "Customer signals", body: "Faster first response and clearer status updates on inbound requests.", color: TOK.blueL, accent: TOK.blue },
          { title: "Leadership view", body: "Decisions land in hours, not days — board prep no longer a Sunday job.", color: TOK.amberL, accent: TOK.amber },
        ].map((f) => (
          <div key={f.title} className="rounded-[10px] p-5 border" style={{ background: f.color, borderColor: TOK.rule }}>
            <div className="text-[14px] font-bold mb-1.5" style={{ color: f.accent }}>{f.title}</div>
            <p className="text-[13px] leading-[1.6]" style={{ color: TOK.ink2 }}>{f.body}</p>
          </div>
        ))}
      </div>

      <SectionRule />

      {/* ════════════ SECTION 05 — Cost Transparency Note ════════════ */}
      <Card className="p-6" data-testid="dashboard-transparency-note">
        <div className="flex items-start gap-3">
          <Database className="h-4 w-4 mt-0.5" style={{ color: TOK.ink3 }} />
          <div>
            <div className="text-[13px] font-bold uppercase tracking-[0.10em]" style={{ color: TOK.ink3 }}>
              Cost &amp; data transparency
            </div>
            <p className="mt-2 text-[14px] leading-[1.7]" style={{ color: TOK.ink2, maxWidth: 760 }}>
              Once {orgName}'s workflows are live, this dashboard will pull real
              token, latency, and cost data from each provider, plus operational
              outcomes from your business systems. Until then, the figures above
              are illustrative — but the workflows, sequencing, and project
              details are real and synced from your AI Workflow Plan.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
